import { AbstractControl, ValidatorFn, Validators } from '@angular/forms';

export class FormValidators {
  // Validator to ensure the input is not only spaces
  static noOnlySpacesValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const value = control.value;
      if (value && typeof value === 'string' && value.trim().length === 0) {
        return { onlySpaces: true };
      }
      return null;
    };
  }

  // Custom validator for minLength excluding spaces
  static minLengthNoSpaces(minLength: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const value = control.value;
      if (!value || typeof value !== 'string') return null;
      const nonSpaceLength = value.replace(/\s/g, '').length;
      if (nonSpaceLength < minLength) {
        return { minlengthNoSpaces: true };
      }
      return null;
    };
  }

  // Custom validator for maxLength excluding spaces
  static maxLengthNoSpaces(maxLength: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const value = control.value;
      if (!value || typeof value !== 'string') return null;
      const nonSpaceLength = value.replace(/\s/g, '').length;
      if (nonSpaceLength > maxLength) {
        return { maxlengthNoSpaces: true };
      }
      return null;
    };
  }

  // Common address pattern validator
  static addressPatternValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      if (!control.value || typeof control.value !== 'string') return null;

      const pattern = /^[a-zA-Z0-9\s\-,\.\/\(\)&#]+$/;
      if (!pattern.test(control.value)) {
        return { invalidPattern: true };
      }
      return null;
    };
  }

  // Validator For Line 1 (required, max 200 characters)
  static addressLine1Validators(): ValidatorFn[] {
    return [
      Validators.required,
      Validators.maxLength(200),
      FormValidators.maxLengthNoSpaces(200),
      FormValidators.noOnlySpacesValidator(),
      FormValidators.addressPatternValidator()
    ]
  }

  // Validators For Line 2 (optional, max 200 characters)
  static addressLine2Validators(): ValidatorFn[] {
    return [
      Validators.maxLength(200),
      FormValidators.maxLengthNoSpaces(200),
      FormValidators.noOnlySpacesValidator(),
      FormValidators.addressPatternValidator()
    ]
  }

  // Validator for City (required, max 100 chars)
  static cityValidators(): ValidatorFn[] {
    return [
      Validators.required,
      Validators.maxLength(100),
      FormValidators.maxLengthNoSpaces(100),
      FormValidators.noOnlySpacesValidator(),
      Validators.pattern(/^[a-zA-Z\s\-]+$/)         // Letter, Spaces and Hyphens Only
    ];
  }

  // Validator for State (required, max 100 chars)
  static stateValidators(): ValidatorFn[] {
    return [
      Validators.required,
      Validators.maxLength(100),
      FormValidators.noOnlySpacesValidator()
    ];
  }

  // Validator to ensure no leading or trailing spaces
  static noLeadingOrTrailingSpacesValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const value = control.value;
      if (value && typeof value === 'string' && (value.startsWith(' ') || value.endsWith(' '))) {
        return { leadingOrTrailingSpaces: true };
      }
      return null;
    };
  }

  // Validator for Postal Code (required, max 12 chars)
  static postalCodeValidators(): ValidatorFn[] {
    return [
      Validators.required,
      Validators.maxLength(12),
      Validators.pattern(/^[A-Za-z0-9 -]{3,12}$/),
      FormValidators.noOnlySpacesValidator(),
      FormValidators.noLeadingOrTrailingSpacesValidator()
    ];
  }

  // Validator for Country (required, max 100 chars)
  static countryValidators(): ValidatorFn[] {
    return [
      Validators.required,
      Validators.maxLength(100),
      FormValidators.noOnlySpacesValidator()
    ];
  }

  // Title Validators For Book/ Page / Chapter Form
  static titleValidators(): ValidatorFn[] {
    return [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(50),
      FormValidators.minLengthNoSpaces(2),
      FormValidators.maxLengthNoSpaces(50),
      Validators.pattern(/^[a-zA-Z0-9 ]+$/), // Letters, numbers, and spaces only
      FormValidators.noOnlySpacesValidator(),
    ];
  }

  // Author Validator for Book
  static authorNameValidators(): ValidatorFn[] {
    return [
      Validators.minLength(2),
      Validators.maxLength(200),
      FormValidators.minLengthNoSpaces(2),
      FormValidators.maxLengthNoSpaces(200),
      Validators.pattern(/^[\p{L}\s]+$/u),        // Only letters and spaces (Unicode aware)
      FormValidators.noOnlySpacesValidator(),
    ];
  }

  // Full Name Validators
  static fullNameValidators(): ValidatorFn[] {
    return [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(50),
      FormValidators.minLengthNoSpaces(2),
      FormValidators.maxLengthNoSpaces(50),
      Validators.pattern(/^[a-zA-Z ]+$/), // Letters and spaces only
      FormValidators.noOnlySpacesValidator(),
    ];
  }

  // Mobile Validators
  static mobileValidators(): ValidatorFn[] {
    return [
      Validators.pattern(/^[0-9]{10}$/), // Exactly 10 digits
      (control: AbstractControl): { [key: string]: boolean } | null => {
        const value = control.value;

        if (!value) return null; // Skip if empty (required validator handles this)

        // Check for alphabetic characters
        if (/[a-zA-Z]/.test(value)) {
          return { invalidMobile: true };
        }

        // Check length explicitly (redundant with pattern but for clarity)
        if (value.length !== 10) {
          return { invalidMobile: true };
        }

        // Disallow leading zero
        if (value.startsWith('0')) {
          return { invalidMobile: true };
        }

        // Disallow repetitive digits (e.g., 1111111111)
        const allSameDigits = /^(\d)\1{9}$/.test(value); // e.g., "1111111111"
        const sequentialAscending = /^[0-9]{10}$/.test(value) &&
          value.split('').every((digit: string, i: number, arr: string[]) =>
            i === 0 || Number(digit) === Number(arr[i - 1]) + 1
          ); // e.g., "1234567890"
        const sequentialDescending = /^[0-9]{10}$/.test(value) &&
          value.split('').every((digit: string, i: number, arr: string[]) =>
            i === 0 || Number(digit) === Number(arr[i - 1]) - 1
          ); // e.g., "9876543210"
        const repeatedPattern = /^[0-9]{10}$/.test(value) &&
          (value.match(/^(\d{2})\1{4}$/) || value.match(/^(\d{5})\1{1}$/)); // e.g., "1212121212", "1234512345"
        const tooManyRepeats = /^[0-9]{10}$/.test(value) &&
          value.split('').some((digit: string, i: number, arr: string[]) => {
            let count: number = 1;
            for (let j: number = i + 1; j < arr.length; j++) {
              if (arr[j] === digit) count++;
              else break;
              if (count >= 6) return true;
            }
            return false;
          }); // e.g., "1111112345"

        if (allSameDigits || sequentialAscending || sequentialDescending || repeatedPattern || tooManyRepeats) {
          return { invalidMobile: true };
        }

        return null;
      }
    ];
  }
  // Email Validators
  static emailValidators(): ValidatorFn[] {
    return [
      Validators.required,
      Validators.email,
      Validators.maxLength(320),
      Validators.pattern(/^[^@]+@[^@]+\.[a-zA-Z]{2,}$/), // Basic domain validation
    ];
  }

  // Date of Birth Validator
  static dobValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const value = control.value;
      const date = new Date(value);
      const today = new Date();
      // Check for future date
      if (date > today) {
        return { futureDate: true };
      }
      // Check for unrealistic date (before 1900)
      if (date.getFullYear() < 1900) {
        return { unrealisticDate: true };
      }
      // Check if user is at least 18 years old
      let age = today.getFullYear() - date.getFullYear();
      const monthDiff = today.getMonth() - date.getMonth();
      const dayDiff = today.getDate() - date.getDate();
      // Adjust age if birthday hasn't occurred this year
      if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        age--;
      }
      if (age < 18) {
        return { underage: true };
      }
      return null;
    };
  }

  // Author Date of Birth Validator
  static authorDOBValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const value = control.value;
      const date = new Date(value);
      const today = new Date();
      // Check for future date
      if (date > today) {
        return { futureDate: true };
      }
      return null;
    };
  }

  // Address Validators
  static addressValidators(): ValidatorFn[] {
    return [
      Validators.minLength(3),
      Validators.maxLength(250),
      FormValidators.minLengthNoSpaces(3),
      FormValidators.maxLengthNoSpaces(250),
      FormValidators.noOnlySpacesValidator(),
    ];
  }

  // GSTIN Validator
  static gstValidators() {
    return [
      // Pattern Accepts Case-Insensitive
      Validators.pattern(/^[0-3][0-9][A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/i), // GSTIN format
      Validators.maxLength(15), // Exact length for GSTIN
      FormValidators.maxLengthNoSpaces(15),
      FormValidators.noOnlySpacesValidator()
    ];
  }
}
